/**
 * Hook for monitoring message status and syncing with workflow planning status
 * Automatically updates workflow when planning message changes state
 *
 * Tracks planning via:
 * 1. Message status (streaming, completed, interrupted, error) - Primary
 * 2. Job progress from message.metadata (percentage, currentOperation) - Real-time updates
 * 3. state.json polling - Secondary/fallback (handled in WorkflowTimeline)
 */

import { useEffect } from 'react';
import { useMessageStore } from '@/stores/messageStore';
import { useWorkflowStore } from '@/stores/workflowStore';

export function useMessageStatusMonitor(conversationId: string) {
  const { getWorkflowState, updatePlanningStatus, updateSlideGenerationStatus } = useWorkflowStore();
  const { getMessageById, streamingMessageId, getMessagesForConversation } = useMessageStore();

  useEffect(() => {
    if (!conversationId) return;

    const workflow = getWorkflowState(conversationId);
    if (!workflow) return;

    const planningMessageId = workflow.steps.planning.planningMessageId;
    if (!planningMessageId) return;

    // Get the planning message
    const message = getMessageById(planningMessageId);
    if (!message) return;

    // Extract job progress if available (updated by chat polling)
    const jobId = message.metadata?.jobId;
    const jobProgress = message.metadata?.progress;

    console.log('🔍 Monitoring planning message:', {
      messageId: planningMessageId,
      status: message.status,
      streamComplete: message.metadata?.streamComplete,
      interrupted: message.metadata?.interrupted,
      isStreaming: streamingMessageId === planningMessageId,
      jobId,
      jobProgress,
    });

    // Check if interrupted by user (highest priority)
    if (message.metadata?.interrupted) {
      console.log('⏸️ Planning message interrupted by user');

      updatePlanningStatus(conversationId, {
        status: 'error',
        error: 'Planning was interrupted by user',
      });
      return;
    }

    // Check for errors
    if (message.status === 'error') {
      console.log('❌ Planning message failed');

      updatePlanningStatus(conversationId, {
        status: 'error',
        error: message.metadata?.error || 'Planning failed',
      });
      return;
    }

    // Check if completed
    if (message.status === 'completed' && message.metadata?.streamComplete) {
      console.log('✅ Planning message completed');

      updatePlanningStatus(conversationId, {
        status: 'completed',
        progress: 100,
        completedAt: new Date(),
        canProceed: true,
      });

      // Enable slide generation step
      updateSlideGenerationStatus(conversationId, {
        canProceed: true,
      });
      return;
    }

    // Check if message is currently streaming or has active job
    if (streamingMessageId === planningMessageId || message.status === 'streaming') {
      // Use job progress if available (more granular than just 50%)
      const progress = jobProgress?.percentage ?? 50;
      const currentOperation = jobProgress?.currentOperation;

      console.log('🔄 Planning in progress:', { progress, currentOperation });

      updatePlanningStatus(conversationId, {
        status: 'in_progress',
        progress,
        ...(currentOperation && { currentOperation }),
      });
      return;
    }

  }, [conversationId, streamingMessageId, getMessagesForConversation]); // Re-run when streaming changes or messages update
}
